// Shaping of the Game Analysis data (/api/game-analysis/*, read from bi-game-analysis) for
// the Game Analysis pages. Every profile value arrives with its source:
//   { value, channel, source, detail?, alternatives?: [{ value, channel, source }], conflict? }
// The channel says how much to trust it, so the pages always show it next to the value.

// Channels in trust order. `variant` is a Badge variant.
export const CHANNELS = {
  measured:   { label: 'Measured',   variant: 'success',     hint: "Read from the game's own data in a real-money session." },
  external:   { label: 'External',   variant: 'purple',      hint: 'From a provider client area or game sheet, entered by the game team.' },
  game_text:  { label: 'Game rules', variant: 'info',        hint: "Stated in the game's own rules or paytable." },
  catalog:    { label: 'Catalog',    variant: 'secondary',   hint: 'From the Bitstarz catalog (games_filter).' },
  derived:    { label: 'Derived',    variant: 'outline',     hint: 'Computed from other values (see detail).' },
  llm:        { label: 'Model',      variant: 'warning',     hint: 'Read from screenshots by a vision model — verify before relying on it.' },
  unverified: { label: 'Unverified', variant: 'destructive', hint: 'Only unverified sources disagree or exist; shown as alternatives, not as a fact.' },
};

export function channelMeta(channel) {
  return CHANNELS[channel] || { label: channel || 'Unknown', variant: 'outline', hint: '' };
}

// Rows of the key-facts table, in the order a game expert reads them.
export const FACT_ROWS = [
  ['rtp', 'RTP'],
  ['rtpVariants', 'RTP variants'],
  ['maxWinX', 'Max win'],
  ['maxWinMoney', 'Max win (money)'],
  ['maxWinCap', 'Max win cap'],
  ['betRange', 'Bet range'],
  ['volatility', 'Volatility'],
  ['hitRate', 'Hit rate'],
  ['payType', 'Lines / ways'],
  ['grid', 'Grid'],
  ['features', 'Features'],
  ['jackpots', 'Jackpots'],
  ['theme', 'Theme'],
  ['studio', 'Studio'],
  ['platform', 'Platform'],
  ['engine', 'Engine'],
];

export function humanize(s) {
  return String(s).replace(/_/g, ' ');
}

export function formatNumber(v, maxFrac = 2) {
  if (v == null || v === '' || Number.isNaN(Number(v))) return '—';
  return Number(v).toLocaleString('en-GB', { maximumFractionDigits: maxFrac });
}

// Money in a fiat currency: €0.20, AU$10.00, NOK 2.50. Bets have up to 4 decimals (0.125).
export function formatMoney(v, currency = 'EUR') {
  if (v == null || v === '' || Number.isNaN(Number(v))) return '—';
  try {
    // Intl separates a currency code with a no-break space; a plain one keeps copied values searchable
    return Number(v).toLocaleString('en-GB', {
      style: 'currency', currency, minimumFractionDigits: 2, maximumFractionDigits: 4,
    }).replace(/\u00a0/g, ' ');
  } catch {
    return `${formatNumber(v, 4)} ${currency}`;
  }
}

export function formatPct(v) {
  return v == null || Number.isNaN(Number(v)) ? '—' : `${formatNumber(v, 2)}%`;
}

export function formatX(v) {
  return v == null || Number.isNaN(Number(v)) ? '—' : `${formatNumber(v, 2)}×`;
}

// One summary value as text; `key` picks the unit.
export function formatFactValue(key, value) {
  if (value == null || (Array.isArray(value) && value.length === 0)) return '—';
  switch (key) {
    case 'rtp':
    case 'hitRate':
      return typeof value === 'number' ? formatPct(value) : String(value);
    case 'maxWinX':
      return typeof value === 'number' ? formatX(value) : String(value);
    case 'maxWinMoney':
    case 'maxWinCap':
      // strop v valuti ({ EUR: 150000 }); starejši profili imajo lahko golo število
      if (typeof value !== 'object') return formatNumber(value);
      return Object.entries(value).map(([cur, v]) => formatMoney(v, cur)).join(' · ');
    case 'rtpVariants':
      return Object.entries(value).map(([mode, v]) => `${humanize(mode)} ${formatPct(v)}`).join(' · ');
    case 'betRange': {
      const range = `${formatMoney(value.min, value.currency)} – ${formatMoney(value.max, value.currency)}`;
      return value.steps ? `${range} · ${value.steps} steps` : range;
    }
    case 'grid':
      return value.cols && value.rows ? `${value.cols}×${value.rows}` : JSON.stringify(value);
    case 'jackpots':
      return formatJackpots(value);
    default:
      if (Array.isArray(value)) return value.map(humanize).join(', ');
      if (typeof value === 'object') return JSON.stringify(value);
      return humanize(value);
  }
}

const jackpotTypeLabel = t => (t === 'fixed' ? 'Fixed' : t === 'progressive' ? 'Progressive' : 'Jackpot');

// Fixed: every tier × bet (sorted low → high by bi-game-analysis). Progressive: the type and the
// network only — a live amount is not a fact of the game, the start amount (per tier or overall) is when
// the rules state it. Progressive with tiers × bet = the game's own fixed tiers beside the network
// (King Millions): "Fixed: Mini 15× / … / Mega 1,000×" (agreed with the game experts 2026-10-23).
export function formatJackpots(j) {
  if (!j || typeof j !== 'object') return String(j ?? '—');
  const type = jackpotTypeLabel(j.type);
  const list = Array.isArray(j.tiers) ? j.tiers : [];
  const tiers = list.map(t => {
    if (t.x != null) return `${t.name} ${formatX(t.x)}`;
    if (t.amount != null) return `${t.name} ${formatNumber(t.amount)}`;
    return t.seed ? `${t.name} from ${t.seed}` : t.name;
  });
  const hybrid = j.type === 'progressive' && list.length > 0 && list.every(t => t.x != null);
  const tierText = hybrid ? `Fixed: ${tiers.join(' / ')}` : tiers.join(' · ');
  return [type, j.network, tierText, j.seed ? `starts at ${j.seed.replace(/^starts at\s*/i, '')}` : '']
    .filter(Boolean)
    .join(' · ');
}

// List cell: "Fixed 1,000×" + tier count; progressive has no top tier (a live amount is not a fact).
export function jackpotCell(g) {
  if (!g?.jackpot_type) return null;
  const type = jackpotTypeLabel(g.jackpot_type);
  return {
    // progressive with a top × = the game's fixed tiers beside the network
    text:
      g.jackpot_top_x == null
        ? type
        : g.jackpot_type === 'progressive'
          ? `${type} + fixed ${formatX(g.jackpot_top_x)}`
          : `${type} ${formatX(g.jackpot_top_x)}`,
    tiers: g.jackpot_tiers ? `${g.jackpot_tiers} tier${g.jackpot_tiers === 1 ? '' : 's'}` : '',
  };
}

// The key-facts table: one row per FACT_ROWS entry the profile knows about. A fact with no trusted
// value still gets a row when it has alternatives — the expert should see what the sources claim.
export function factRows(summary) {
  if (!summary) return [];
  const rows = [];
  for (const [key, label] of FACT_ROWS) {
    const f = summary[key];
    if (!f) continue;
    const alternatives = (f.alternatives || []).map(a => ({
      text: formatFactValue(key, a.value), channel: a.channel, source: a.source,
    }));
    if (f.value == null && alternatives.length === 0) continue;
    rows.push({
      key,
      label,
      text: formatFactValue(key, f.value),
      channel: f.channel,
      source: f.source || '',
      detail: f.detail || '',
      conflict: f.conflict || '',
      alternatives,
    });
  }
  return rows;
}

// Bets per currency: captured and failed first (EUR on top), crypto and other uncaptured counted apart.
export function currencyRows(currencies) {
  const list = Array.isArray(currencies) ? currencies : [];
  const shown = list.filter(c => c.status !== 'not_captured');
  shown.sort((a, b) => (a.currency === 'EUR' ? -1 : b.currency === 'EUR' ? 1 : a.currency.localeCompare(b.currency)));
  return {
    rows: shown.map(c => ({
      currency: c.currency,
      status: c.status,
      min: c.status === 'captured' ? formatMoney(c.min, c.currency) : '—',
      max: c.status === 'captured' ? formatMoney(c.max, c.currency) : '—',
      steps: Array.isArray(c.ladder) ? c.ladder.length : null,
      ladder: (c.ladder || []).map(v => formatMoney(v, c.currency)),
      vsEUR: vsEURText(c.vsEUR),
      note: c.error || c.note || '',
    })),
    notCaptured: list.filter(c => c.status === 'not_captured').map(c => c.currency),
  };
}

// How a currency's ladder relates to EUR's: same steps × factor (or which steps differ), or a ladder converted at an
// exchange rate and rounded (no common factor). Bet limits (min / max) are shown whenever they scale differently.
export function vsEURText(vs) {
  if (!vs) return '';
  const factor = vs.factor != null ? `×${formatNumber(vs.factor, 4)} of EUR` : '';
  const differs = x => x != null && (vs.factor == null || Math.abs(x / vs.factor - 1) > 0.005);
  const limits = [
    differs(vs.minX) ? `min ×${formatNumber(vs.minX, 3)}` : '',
    differs(vs.maxX) ? `max ×${formatNumber(vs.maxX, 3)}` : '',
  ].filter(Boolean).join(', ');
  if (vs.kind === 'converted') return ['converted & rounded, no common factor', limits && `${limits} of EUR`].filter(Boolean).join(' · ');
  if (vs.kind === 'same') return factor || 'same as EUR';
  const parts = [];
  if (vs.extra?.length) parts.push(`+${vs.extra.map(v => formatNumber(v, 4)).join(', ')}`);
  if (vs.missing?.length) parts.push(`−${vs.missing.map(v => formatNumber(v, 4)).join(', ')}`);
  const where = vs.kind === 'edges' ? 'differs at the ends' : 'differs in between';
  return [factor, `${where}${parts.length ? ` (${parts.join(' ')})` : ''}`, limits].filter(Boolean).join(' · ');
}

const BONUS_KINDS = { ante: 'Ante bet', bonus_buy: 'Bonus buy', feature_spins: 'Feature spins' };

export function bonusBuyRows(bonusBuy) {
  return (Array.isArray(bonusBuy) ? bonusBuy : []).map((b, i) => {
    const range = b.priceRange?.value;
    return {
      id: `${i}-${b.name}`,
      name: b.name || 'Feature',
      // the same option under another name in another source (rules vs. buy menu)
      aliases: Array.isArray(b.aliases) ? b.aliases : [],
      kind: BONUS_KINDS[b.kind] || (b.kind ? humanize(b.kind) : 'Feature'),
      description: b.description || '',
      cost: b.costX ? { text: formatX(b.costX.value), channel: b.costX.channel, source: b.costX.source, detail: b.costX.detail || '' } : null,
      price: b.price ? { text: formatMoney(b.price.value, 'EUR'), channel: b.price.channel, source: b.price.source } : null,
      priceRange: range
        ? {
          text: `${formatMoney(range.min, range.currency)} – ${formatMoney(range.max, range.currency)}`,
          channel: b.priceRange.channel,
          source: b.priceRange.source,
          detail: b.priceRange.detail || '',
          ladder: (range.ladder || []).map(s => ({ bet: formatMoney(s.bet, range.currency), price: formatMoney(s.price, range.currency) })),
        }
        : null,
      rtp: b.rtp ? { text: formatPct(b.rtp.value), channel: b.rtp.channel, source: b.rtp.source } : null,
      available: b.available,
    };
  });
}

// Symbol pays as multiples of the bet, highest count first: "5: 20× · 4: 8× · 3: 1×".
export function symbolRows(symbolsX) {
  return (Array.isArray(symbolsX) ? symbolsX : []).map(s => {
    const pays = [...(s.pays?.value || [])].sort((a, b) => Number(b.count) - Number(a.count));
    return {
      name: s.name,
      pays: pays.map(p => `${p.count}: ${formatX(p.x)}`).join(' · ') || '—',
      channel: s.pays?.channel,
      source: s.pays?.source || '',
      detail: s.pays?.detail || '',
    };
  });
}

export const ISSUE_LEVELS = {
  bad:  { label: 'Failed',  variant: 'destructive', order: 0 },
  warn: { label: 'Warning', variant: 'warning',     order: 1 },
  info: { label: 'Info',    variant: 'secondary',   order: 2 },
};

export function sortIssues(issues) {
  return [...(issues || [])].sort((a, b) => (ISSUE_LEVELS[a.level]?.order ?? 9) - (ISSUE_LEVELS[b.level]?.order ?? 9));
}

export function issueCounts(issues) {
  const counts = { bad: 0, warn: 0, info: 0 };
  for (const i of issues || []) if (counts[i.level] != null) counts[i.level] += 1;
  return counts;
}

export function formatCompleteness(v) {
  return v == null ? '—' : `${Math.round(Number(v) * 100)}%`;
}

export function formatDate(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? '—'
    : d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

// A link from the data (external facts are imported from sheets): only http(s), never javascript: or data:.
export function safeHttpUrl(url) {
  if (typeof url !== 'string') return null;
  try {
    const u = new URL(url);
    return u.protocol === 'https:' || u.protocol === 'http:' ? u.href : null;
  } catch {
    return null;
  }
}

// Screenshots are <img> sources, which cannot carry the X-Brand header — the brand goes in ?brand=.
export function shotUrl(slug, file, brand) {
  const base = `/api/game-analysis/games/${encodeURIComponent(slug)}/shots/${encodeURIComponent(file)}`;
  return brand ? `${base}?brand=${encodeURIComponent(brand)}` : base;
}

// List columns that sort on the server, by the API's sort key.
export const SORT_KEYS = {
  title: 'title',
  rtp: 'rtp',
  max_win_x: 'max_win',
  completeness: 'completeness',
  assembled_at: 'assembled',
  issues: 'problems',
  jackpot: 'jackpot',
};

// The direction a column starts in when first clicked: names A→Z, numbers and dates high first.
export function defaultSortDir(col) {
  return col === 'title' ? 'asc' : 'desc';
}

export const PAGE_SIZE = 50;

export function gamesQuery({ query, provider, sortCol, sortDir, page, problemsOnly = false, jackpot = 'all', jackpotTiers = 0 }) {
  return {
    query: query.trim() || undefined,
    provider: provider && provider !== 'all' ? provider : undefined,
    problems: problemsOnly ? 'true' : undefined,
    jackpot: jackpot && jackpot !== 'all' ? jackpot : undefined,
    jackpot_tiers: jackpotTiers > 0 ? jackpotTiers : undefined,
    sort: SORT_KEYS[sortCol] || 'title',
    desc: sortDir === 'desc' ? 'true' : undefined,
    limit: PAGE_SIZE,
    offset: (page - 1) * PAGE_SIZE,
  };
}

// Classification facets ({ grid: [...], bonusFeatures: [...] }) as label → text rows.
export function facetRows(facets) {
  return Object.entries(facets || {})
    .filter(([, v]) => Array.isArray(v) ? v.length > 0 : v != null)
    .map(([k, v]) => ({
      label: humanize(k.replace(/([a-z])([A-Z])/g, '$1 $2')).toLowerCase(),
      text: Array.isArray(v) ? v.map(humanize).join(', ') : humanize(v),
    }));
}
